import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_STRENGTH_LABELS,
  assessPasswordStrength,
  scorePassword,
} from '../shared/password-policy';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['../change-password/change-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit {
  readonly passwordMinLength = PASSWORD_MIN_LENGTH;

  token = '';
  newPassword = '';
  confirmPassword = '';
  loading = false;
  completed = false;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private snackbar: SnackbarService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const fragment = this.route.snapshot.fragment || '';
    this.token = new URLSearchParams(fragment).get('token') || '';
  }

  get passwordScore(): number {
    return scorePassword(this.newPassword);
  }

  get passwordScoreLabel(): string {
    return PASSWORD_STRENGTH_LABELS[this.passwordScore] || '';
  }

  get passwordPolicyMessage(): string | null {
    if (!this.newPassword) {
      return null;
    }

    const assessment = assessPasswordStrength(this.newPassword);
    return assessment.valid
      ? null
      : assessment.message || 'Contraseña no válida';
  }

  get mismatch(): boolean {
    return (
      this.confirmPassword.length > 0 &&
      this.newPassword !== this.confirmPassword
    );
  }

  async submit(): Promise<void> {
    if (this.loading) {
      return;
    }

    if (!this.token) {
      this.snackbar.error('El enlace no es válido o ha caducado');
      return;
    }

    if (!this.newPassword || !this.confirmPassword) {
      this.snackbar.warning('Rellena los dos campos');
      return;
    }

    const assessment = assessPasswordStrength(this.newPassword);
    if (!assessment.valid) {
      this.snackbar.warning(
        assessment.message || 'La contraseña no cumple la política'
      );
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.snackbar.warning('La confirmación no coincide');
      return;
    }

    this.loading = true;
    try {
      await this.authService.resetPassword(this.token, this.newPassword);
      this.completed = true;
      this.token = '';
      this.newPassword = '';
      this.confirmPassword = '';
      await this.router.navigate([], {
        relativeTo: this.route,
        fragment: undefined,
        replaceUrl: true,
      });
    } catch (error: any) {
      this.snackbar.error(
        error?.error?.message || 'No se pudo restablecer la contraseña',
        { duration: 5000 }
      );
    } finally {
      this.loading = false;
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
